import React from 'react';
import { Link } from 'react-router-dom';
import { Box , Typography, Button } from '@mui/material';
import { useAppSelector } from '../store';
import { selectApiStatus, selectPerformanceMetrics } from '../store/selectors';
import '../styles/HomePage.css';

export const HomePage: React.FC = () => {
  const apiStatus = useAppSelector(selectApiStatus);
  const performanceMetrics = useAppSelector(selectPerformanceMetrics);

  return (
    <Box className="home-page">
      <Typography variant="h2" sx={{color: 'text.primary'}}>Welcome to Patient Pro</Typography>
      
      <Box className="features-grid">
        <Box className="feature-card" sx={{
          backgroundColor: 'background.paper' ,
          '&:before': {
            background: (theme) => `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          }
          }}>
          <Typography variant="h3" sx={{color: 'text.primary'}}>AI-Powered Diagnostics</Typography>
          <Typography variant="body1" sx={{color: 'text.primary'}}>Practice medical interviews with AI-simulated patients</Typography>
          <Button
            component={Link}
            to="/diagnostic"
            variant="contained"
            sx={{
              backgroundColor: 'primary.main',
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: 'primary.dark',
                color: 'text.primary',
              }
            }}
          >
            Start Diagnosis
          </Button>
        </Box>
        
        <Box className="feature-card" sx={
          {backgroundColor: 'background.paper' ,
          '&:before': {
            background: (theme) => `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          }
          }}>
          <Typography variant="h3" sx={{color: 'text.primary'}}>Case Library</Typography>
          <Typography variant="body1" sx={{color: 'text.primary'}}>Browse and manage medical fields and cases</Typography>
          <Button
            component={Link}
            to="/cases"
            variant="contained"
            sx={{
              backgroundColor: 'primary.main',
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: 'primary.dark',
                color: 'text.primary',
              }
            }}
          >
            View Cases
          </Button>
        </Box>
        
        <Box className="feature-card" sx={
          {backgroundColor: 'background.paper' ,
          '&:before': {
            background: (theme) => `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          }
          }}>
          <Typography variant="h3" sx={{color: 'text.primary'}}>Performance Analytics</Typography>
          <Typography variant="body1" sx={{color: 'text.primary'}}>Track your diagnostic accuracy and completeness</Typography>
          <Button
            component={Link}
            to="/statistics"
            variant="contained"
            sx={{
              backgroundColor: 'primary.main',
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: 'primary.dark',
                color: 'text.primary',
              }
            }}
          >
            View Stats
          </Button>
        </Box>
      </Box>

      {/* Current Session Stats */}
      {performanceMetrics && (
        <Box className="quick-stats" sx={{backgroundColor: 'background.paper'}}>
          <Box className="stat-item">
            <Typography variant="body1" sx={{color: 'text.primary'}}>{performanceMetrics.questionsAsked}</Typography>
            <Typography variant="body1" sx={{color: 'text.primary'}}>Questions Asked</Typography>
          </Box>
          <Box className="stat-item">
            <Typography variant="body1" sx={{color: 'text.primary'}}>{performanceMetrics.responsesReceived}</Typography>
            <Typography variant="body1" sx={{color: 'text.primary'}}>Responses</Typography>
          </Box>
          <Box className="stat-item">
            <Typography variant="body1" sx={{color: 'text.primary'}}>{Math.floor(performanceMetrics.sessionDuration / 60000)}m</Typography>
            <Typography variant="body1" sx={{color: 'text.primary'}}>Session Duration</Typography>
          </Box>
        </Box>
      )}
      
      <Box className="status-section" sx={{backgroundColor: 'background.paper'}}>
        <Typography variant="h3" sx={{color: 'text.primary'}}>System Status</Typography>
        <Box component="ul">
                      <Box component="li" sx={{ 
              backgroundColor: 'background.default',
              color: 'text.primary',
              padding: '8px 12px',
              marginBottom: '4px',
              borderRadius: '4px'
            }}>
              OpenAI: {apiStatus?.openai ? '✅' : '❌'}
            </Box>
            <Box component="li" sx={{ 
              backgroundColor: 'background.default',
              color: 'text.primary',
              padding: '8px 12px',
              marginBottom: '4px',
              borderRadius: '4px'
            }}>
              Deepgram: {apiStatus?.deepgram ? '✅' : '❌'}
            </Box>
            <Box component="li" sx={{ 
              backgroundColor: 'background.default',
              color: 'text.primary',
              padding: '8px 12px',
              marginBottom: '4px',
              borderRadius: '4px'
            }}>
              Anam: {apiStatus?.anam ? '✅' : '❌'} | Tavus: {apiStatus?.tavus ? '✅' : '❌'}
            </Box>
        </Box>
      </Box>
    </Box>
  );
};
